import { Lang } from 'vuejs-localization';
import { injectable } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { container } from '@/app-modules/_ioc';
import { ConfigurationPaths, ConfigurationService } from '@/app-modules/config';
import Vue from 'vue';


class Country {
  constructor(public id: string, public name: string, public langCode?: string) {
    this.langCode = (langCode ? langCode : id).toLowerCase();
  }
}

interface ILocalisationService {
  getLangInstance(): Lang;
  getCountries(): Promise<Country[]>;
  findCountryById(countryId: string): Promise<Country>;
  setLangByCountryId(countryId: string, lang?: Lang): Promise<void>;
  refreshCurrentLang(lang: Lang): void;
}

@injectable()
class LocalisationService implements ILocalisationService {
  private readonly countries: Country[];
  private $lang: Lang;

  constructor() {
    this.initialiseLangInstance();
  }

  public getLangInstance(): Lang {
    this.initialiseLangInstance();
    return this.$lang;
  }

  public async getCountries(): Promise<Country[]> {
    return await this.getCountriesByTenant();
  }

  public async findCountryById(countryId: string): Promise<Country> {
    const countriesForTenant = await this.getCountriesByTenant();
    return countriesForTenant.find((o) => o.id === countryId);
  }

  public async setLangByCountryId(countryId: string, lang?: Lang): Promise<void> {
    const country = await this.findCountryById(countryId);
    if (country) {
      const langCode = (country.langCode || country.id).toLowerCase();
      this.$lang.setLang(langCode);
      this.refreshCurrentLang(lang);
    }
  }

  public refreshCurrentLang(lang: Lang): void {
    const configService = container.get(SERVICE_IDENTIFIERS.IConfigurationService) as ConfigurationService;
    const country = configService.getUserPreferredCountry();
    if (country) {
      lang.setLang(country);
    }
    else if (lang) {
      const currentLang = this.$lang.getLang();
      lang.setLang(currentLang);
    }

  }

  private async getCountriesByTenant(): Promise<Country[]> {
    const configService = container.get(SERVICE_IDENTIFIERS.IConfigurationService) as ConfigurationService;
    const currentTenantIsInternational = configService.currentEnvironmentTenantIsInternational();

    if (currentTenantIsInternational) {
      return [
        new Country('ES', this.$lang.login.country.spain, 'es'),
        new Country('IT', this.$lang.login.country.italy, 'it'),
        new Country('PT', this.$lang.login.country.portugal, 'pt'),
        new Country('DK', this.$lang.login.country.denmark, 'dk'),
        new Country('SE', this.$lang.login.country.sweden, 'se'),
        new Country('NL', this.$lang.login.country.netherlands, 'nl')
      ];
    }

    return [
      new Country('UK', this.$lang.login.country.uk, 'en'),
    ];
  }

  private initialiseLangInstance() {
    if (!this.$lang) {
      const vueInstance = new Vue();
      this.$lang = vueInstance.$lang;
    }
  }
}

export { ILocalisationService, LocalisationService, Country };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/localisation-service/localisation-service.ts